// Delegate — the headless hook moved to `@heidi/workspace/data` (D2,
// ARCFE-1062). This file owns the app reactions (ARCFE-921): the COMPOSITE
// `invalidateCurrentUser()`, the COMMERCE `invalidateCurrentUserBillingInfo()`,
// the DESKTOP `bulkSyncBridge.invalidateNoteDefaultsCache()` behind
// `isTauri()`, and the HCP verification `invalidateHcpVerificationStatus()`
// after a country write. All four are sequenced here per delivery section 3 —
// the owning side exports the command and app integration sequences it — and
// none is injected through `configureWorkspaceData` (design D7's hidden-edge
// ban).
//
// The `noteDefaultsChanged` DATA branch stayed in the package; only the
// `isTauri()` PLATFORM half of the legacy `noteDefaultsChanged && isTauri()`
// guard is here. Both are pure predicates, so the short-circuit is unchanged.
import { bulkSyncBridge } from '@/tauri/chronicle/bulk-sync';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { invalidateHcpVerificationStatus } from '@heidi/ads/data';
import {
  type UseMutateUpdateUserOptions,
  useMutateUpdateUser as useMutateUpdateUserCore,
} from '@heidi/workspace/data';
import { invalidateCurrentUser } from '../query/useQueryUser';
import { invalidateCurrentUserBillingInfo } from '../query/useQueryUserBillingInfo';

export function useMutateUpdateUser({
  enableInvalidation = true,
  onUpdateUserSuccess,
}: { enableInvalidation?: boolean } & Pick<
  UseMutateUpdateUserOptions,
  'onUpdateUserSuccess'
> = {}) {
  return useMutateUpdateUserCore({
    enableInvalidation,
    onUpdateUserSuccess: (variables) => {
      onUpdateUserSuccess?.(variables);
      // The backend gates HCP status on its saved country, but `onMutate` puts
      // the new country in the cached user before PATCH /user lands. A status
      // read in that gap is a 403 that would otherwise stay cached. That write
      // ignores `enableInvalidation`, so this refetch must ignore it too.
      if (variables.country !== undefined) {
        void invalidateHcpVerificationStatus();
      }
    },
    onUserUpdated: () => {
      void invalidateCurrentUser();
      void invalidateCurrentUserBillingInfo();
    },
    onNoteDefaultsChanged: () => {
      // Invalidate the bulk-sync engine's cached note defaults when any
      // cached field changes. Desktop-only — `isTauri()` short-circuits
      // on web. The Rust cache stores default_template_id,
      // default_voice_style, and brain; refreshes on the next bulk-sync.
      if (isTauri()) {
        void bulkSyncBridge.invalidateNoteDefaultsCache().catch((err) => {
          logger.warn('Failed to invalidate session_engine note defaults cache:', err);
        });
      }
    },
    onUpdateUserError: () => {
      void invalidateCurrentUser();
      void invalidateCurrentUserBillingInfo();
    },
  });
}
